import { SesamiDayObject, SesamiSDKGlobal, SesamiSDKPrimative } from '@/@types/calendar';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import CalendarWeek from './CalendarWeek';
import {
  calendarLoadingAtom,
  dayAvailabilitiesAtom,
  productSesamisAtom,
  selectedMonthAtom,
  selectedProductAtom,
} from './calendar-atoms';

/**
 * @param {number} month - The month number, 0 based
 * @param {number} year - The year, not zero based, required to account for leap years
 * @return {Date[]} List with date objects for each day of the month
 */
function getDaysInMonth(month: number, year: number): Date[] {
  var date = new Date(year, month, 1);
  var days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}

function CalendarGrid() {
  const [dayAvailabilities, setDayAvailabilities] = useAtom(dayAvailabilitiesAtom);

  const selectedProduct = useAtomValue(selectedProductAtom);

  const selectedMonth = useAtomValue(selectedMonthAtom);

  const productSesamis = useAtomValue(productSesamisAtom);

  const [sesami, setSesami] = useState<SesamiSDKGlobal | null>(null);

  const [monthRows, setMonthRows] = useState<SesamiDayObject[][]>([]);

  const setLoading = useSetAtom(calendarLoadingAtom);

  useEffect(() => {
    const sesami = productSesamis.get(selectedProduct);
    if (sesami) {
      setSesami(sesami);
    }
  }, [selectedProduct, selectedMonth, productSesamis]);

  // Build a stable month grid from raw dates and expose getAvailabilities via the shared atom
  useEffect(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const dates = getDaysInMonth(month, year);

    const today = new Date();

    const buildDay = (date: Date, isCurrentMonth: boolean): SesamiDayObject => ({
      date,
      isCurrentMonth,
      isCurrentWeek: false,
      isPast: new Date(date.toDateString()) < new Date(today.toDateString()),
      isToday: date.toDateString() === today.toDateString(),
      getAvailabilities: async () => {
        const byVariant = dayAvailabilities.get(selectedProduct);
        const slots = byVariant?.get(date.toDateString()) || [];
        return slots;
      },
      onLoad: () => {},
      onReady: () => {},
    });

    const allDays: SesamiDayObject[] = [];

    // Leading placeholders based on first day index (Sunday = 0)
    const firstDayIndex = new Date(year, month, 1).getDay();
    if (firstDayIndex > 0) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const prevDates = getDaysInMonth(prevMonth, prevYear);
      const tail = prevDates.slice(prevDates.length - firstDayIndex);
      tail.forEach((d) => allDays.push(buildDay(d, false)));
    }

    // Current month days
    dates.forEach((d) => allDays.push(buildDay(d, true)));

    // Trailing placeholders to complete weeks
    const remainder = allDays.length % 7;
    if (remainder !== 0) {
      const toAdd = 7 - remainder;
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      const nextDates = getDaysInMonth(nextMonth, nextYear);
      for (let i = 0; i < toAdd; i++) {
        allDays.push(buildDay(nextDates[i], false));
      }
    }

    // Chunk into rows of 7
    const rows: SesamiDayObject[][] = [];
    for (let i = 0; i < allDays.length; i += 7) {
      rows.push(allDays.slice(i, i + 7));
    }

    setMonthRows(rows);
  }, [selectedMonth, dayAvailabilities, selectedProduct]);

  useEffect(() => {
    if (!sesami) {
      return;
    }

    if (!sesami.accesstoken) {
      // WHY SESAMI NO GIVE TYPES
      (sesami as unknown as SesamiSDKPrimative).init();
    }

    setLoading(true);

    // Load all availabilities in bulk
    const tasks = sesami.data.rows.flatMap((row) =>
      row.map((day) =>
        day.getAvailabilities().then((availabilities) => ({ date: day.date, availabilities })),
      ),
    );

    Promise.allSettled(tasks).then((results) => {
      const next = new Map(dayAvailabilities);
      results.forEach((res) => {
        if (res.status === 'fulfilled') {
          let variantAvailabilities = next.get(selectedProduct);
          if (!variantAvailabilities) {
            variantAvailabilities = new Map();
            next.set(selectedProduct, variantAvailabilities);
          }

          variantAvailabilities.set(res.value.date.toDateString(), res.value.availabilities);
        }
      });
      setDayAvailabilities(next);
      setLoading(false);
    });
  }, [sesami, selectedMonth, selectedProduct]);

  useEffect(() => {
    console.debug(dayAvailabilities);
  }, [dayAvailabilities]);

  return (
    <div className="medispa-calendar_grid">
      <div className="medispa-calendar_days">
        {monthRows.map((row) => (
          <CalendarWeek key={row[0].date.toISOString()} row={row} />
        ))}
      </div>
    </div>
  );
}

export default CalendarGrid;
